import { PUBLIC_CALLMEBACK, PUBLIC_SF_ORG_ID } from '$env/static/public';
import type { CallmebackData, CallmebackResults } from './types';
import Holidays from "date-holidays";

// Definizione degli orari di apertura
const openingHours: Record<number, { start: [number, number]; end: [number, number] }> = {
  1: { start: [9, 0], end: [20, 0] }, // Lunedì
  2: { start: [9, 0], end: [20, 0] }, // Martedì
  3: { start: [9, 0], end: [20, 0] }, // Mercoledì
  4: { start: [9, 0], end: [20, 0] }, // Giovedì
  5: { start: [9, 0], end: [20, 0] }, // Venerdì
  6: { start: [9, 0], end: [13, 0] }, // Sabato
};
export class ContactUtils {
  private static CALLMEBACK_API = PUBLIC_CALLMEBACK;

  private static getData = async (_fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }): Promise<CallmebackResults> => {
    const res = await _fetch(ContactUtils.CALLMEBACK_API);
    if (res.status !== 200) {
      return Promise.reject(res.json());
    } else {
      return Promise.resolve(res.json());
    }
  };

  public static sendData = async (data: CallmebackData) => {
    const res = await fetch(ContactUtils.CALLMEBACK_API, {
      method: 'POST',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data),
    });

    if (res.status !== 200) {
      return Promise.reject(res.json());
    } else {
      return Promise.resolve(res.json());
    }
  };

  public static sendDataSF = async (data: CallmebackData) => {

    const formatPhoneNumber = data.phone.startsWith("+39") ? data.phone : "+39" + data.phone;

    const fields: any = {
      orgid: PUBLIC_SF_ORG_ID,
      retURL: "https://www.24orebs.com/",
      name: data.name,
      email: data.email || "", 
      phone: formatPhoneNumber,
      reason: data.reason,
      "Data_CallMeBack__c": data.day, // Data
      "Ora_CallMEBack__c": data.hour, // Ora
      "Consenso_digited_CallMeBack__c": data.subscribe ? "1" : "0",
    };

    (function () {
      const IFRAME_ID = 'JLA_API';
    
      // Crea e aggiungi l'iframe nascosto se non esiste già
      if (!document.getElementById(IFRAME_ID)) {
        const iframe = document.createElement("iframe");
        iframe.id = IFRAME_ID;
        iframe.name = IFRAME_ID;
        iframe.src = 'about:blank';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }
    
      // Aggiunge il campo retURL ai dati del form
      fields['retURL'] = window.location.href;
    
      // Crea il form
      const form = document.createElement("form");
      form.method = "POST";
      form.action = ContactUtils.CALLMEBACK_API;
      form.target = IFRAME_ID;
    
      // Aggiunge i campi al form
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value as string;
        form.appendChild(input);
      });
    
      // Aggiunge il form al DOM, lo invia e poi lo rimuove
      document.body.appendChild(form);
      form.submit();
      form.remove();
    })();

    return true;
  };

  public static getAvailableDays = async (_fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }) => {
    const data = await ContactUtils.getData(_fetch);
    return data;
  };

  public static toFormattedDate = (timestamp: number) => {
    const date = new Date(timestamp);
    let formatter = new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'long',
    });
    return formatter.format(date);
  };

  public static getNextAvailableDate(): { value: string; label: string }[] {
    const now = new Date();
    const day: number = now.getDay(); // 0 = Domenica, 1 = Lunedì, ..., 6 = Sabato
    const hour: number = now.getHours();
    const minute: number = now.getMinutes();
  
    const hd = new Holidays("IT");
  
    function isHoliday(date: Date): boolean {
      return !!hd.isHoliday(date);
    }
  
    function isOpen(): boolean {
      if (!openingHours[day]) return false;
      const { start, end } = openingHours[day];
      const currentTime: number = hour * 60 + minute;
      const startTime: number = start[0] * 60 + start[1];
      const endTime: number = end[0] * 60 + end[1];
      return currentTime >= startTime && currentTime <= endTime;
    }
  
    function getNextWorkingDay(date: Date): Date {
      do {
        date.setDate(date.getDate() + 1);
      } while (!openingHours[date.getDay()] || isHoliday(date));
      return date;
    }
  
    let selectedDate: Date = now;
  
    if (hour > 18 || (hour === 18 && minute >= 45) || !isOpen() || isHoliday(now)) {
      selectedDate = getNextWorkingDay(now);
    }
  
    // Formattazione per il label (giorno e mese in italiano)
    const label: string = selectedDate.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
    });
  
    // Formattazione per il value nel formato YYYY-MM-DD
    const value: string = selectedDate.toISOString().split('T')[0];
  
    return [{ value, label }];
  }

  public static getAvailableTimeSlots(selectedDate: string): { value: string; label: string }[] {
    const date = new Date(selectedDate);
    const day: number = date.getDay(); // 0 = Domenica, 1 = Lunedì, ..., 6 = Sabato

    if (!openingHours[day]) return []; // Se il giorno è domenica o non è nell'elenco, nessun orario disponibile.

    const { start, end } = openingHours[day];

    // Orario corrente
    const now = new Date();
    let firstSlotHour: number, firstSlotMinute: number;

    if (date.toDateString() === now.toDateString()) {
      // Se la data selezionata è oggi, calcola il primo slot disponibile
      const nextHour = now.getHours() + 1;
      const nextMinute = Math.ceil(now.getMinutes() / 15) * 15; // Arrotonda ai 15 minuti successivi

      if (nextMinute === 60) {
        firstSlotHour = nextHour + 1;
        firstSlotMinute = 0;
      } else {
        firstSlotHour = nextHour;
        firstSlotMinute = nextMinute;
      }
    } else {
      // Se la data selezionata è futura, parte dall'orario di apertura
      firstSlotHour = start[0];
      firstSlotMinute = start[1];
    }

    // Definisce il primo slot disponibile
    const firstSlot = new Date(date);
    firstSlot.setHours(firstSlotHour, firstSlotMinute, 0, 0);

    // Definisce l'orario massimo disponibile (15 minuti prima della chiusura)
    const lastSlot = new Date(date);
    lastSlot.setHours(end[0], end[1] - 15, 0, 0); // Riduce di 15 minuti

    // Se il primo slot calcolato è fuori dall'orario di apertura, parte dall'orario di apertura
    if (firstSlot < new Date(date.setHours(start[0], start[1], 0, 0))) {
      firstSlot.setHours(start[0], start[1], 0, 0);
    }

    // Se il primo slot supera il tempo massimo disponibile, non ci sono slot disponibili
    if (firstSlot > lastSlot) return [];

    const slots: { value: string; label: string }[] = [];

    while (firstSlot <= lastSlot) {
      const timeString = firstSlot.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

      slots.push({
        value: timeString, // Qui il value è nel formato HH:MM
        label: timeString,
      });

      // Aggiunge 15 minuti
      firstSlot.setMinutes(firstSlot.getMinutes() + 15);
    }

    return slots;
  }

  public static getAvailableHoursInDay = (
    data: CallmebackResults,
    day: number
  ) => {
    let availableHours = [];

    const selectedDay = data.nextDays.find((d) => d.day === day);
    if (selectedDay) {
      for (let i = selectedDay.hourFrom; i <= selectedDay.hourTo; i += 15) {
        availableHours.push(i);
      }
    }

    availableHours = availableHours.filter((hour) => {
      return !data.unavailable[day].includes(hour);
    });

    return availableHours;
  };

  public static toHour = (num) => {
    var hours = Math.floor(num / 60);
    var minutes = num % 60;
    return (
      hours.toLocaleString('it-It', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      }) +
      ':' +
      minutes.toLocaleString('it-It', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })
    );
  };

  public static canDirectCall() {
    const today = new Date();
    const hour = today.getHours();
    const minutes = today.getMinutes();

    if (
      (today.getDay() == 6 &&
        (hour < 9 ||
          (hour == 9 && minutes < 30) ||
          hour > 12 ||
          (hour == 12 && minutes > 0))) ||
      (today.getDay() !== 0 &&
        (hour < 9 ||
          (hour == 9 && minutes < 30) ||
          hour > 18 ||
          (hour == 18 && minutes > 0)))
    ) {
      return false;
    } else {
      return true;
    }
  }
}
